import React from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  InputAdornment,
} from "@mui/material";
import {
  Search as SearchIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
} from "@mui/icons-material";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import SubCategoryItem from "src/components/core-components/mui/SubCategoryItem";
import { useTranslate } from "src/locales";
import { ByzMuiTypography } from "src/components/core-components/mui/inputs/ByzMuiTypography";

interface SidebarAreaProps {
  title: string;
  upn?: string;
  description: string;
  protocolId: number | null;
  sidebarSearchText: string;
  setSidebarSearchText: (value: string) => void;
  mainCategories: any[];
  selectedMainCategory: string;
  handleMainCategorySelect: (id: string) => void;
  visibleCategories: any[];
  expandedCategories: string[];
  selectedSubCategory: string;
  handleSubCategorySelect: (id: string) => void;
  handleCategoryToggle: (id: string) => void;
}

const SidebarArea: React.FC<SidebarAreaProps> = ({
  title,
  upn,
  description,
  protocolId,
  sidebarSearchText,
  setSidebarSearchText,
  mainCategories,
  selectedMainCategory,
  handleMainCategorySelect,
  visibleCategories,
  expandedCategories,
  selectedSubCategory,
  handleSubCategorySelect,
  handleCategoryToggle,
}) => {
  const { t } = useTranslate();

  return (
    <Box sx={{ width: 300, borderRight: 1, borderColor: "divider", p: 2 }}>
      <ByzMuiTypography isTitle={true} sx={{ mb: 3 }}>
        {t("patient-request-service.add-service")}
      </ByzMuiTypography>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <ByzMuiTypography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem", mr: 0.8 }}>
            {t("patient-request-service.patient-info.name-surname")}
          </ByzMuiTypography>
          <ByzMuiTypography variant="body2" sx={{ fontWeight: "bold", fontSize: "0.75rem" }}>
            {title}
          </ByzMuiTypography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <ByzMuiTypography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem", mr: 0.8 }}>
            {t("patient-request-service.patient-info.file-protocol")}
          </ByzMuiTypography>
          <ByzMuiTypography variant="body2" sx={{ fontWeight: "bold", fontSize: "0.75rem" }}>
            {upn || description} {protocolId ? ` / ${protocolId}` : ""}
          </ByzMuiTypography>
        </Box>
      </Box>

      <ByzMuiTextField
        fullWidth
        size="small"
        placeholder={t("patient-request-service.search.placeholder")}
        value={sidebarSearchText}
        onChange={(e) => setSidebarSearchText(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      <Box sx={{ backgroundColor: "#f6f6f6", borderRadius: 1, p: 1 }}>
        <List sx={{ p: 0 }}>
          {mainCategories.map((category) => (
            <ListItem key={category.id} sx={{ p: 0, mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleMainCategorySelect(category.id)}
                sx={{
                  borderRadius: 1,
                  backgroundColor: "transparent",
                  color: selectedMainCategory === category.id ? "primary.main" : "inherit",
                  fontWeight: selectedMainCategory === category.id ? "bold" : "normal",
                  pl: 1,
                  pr: 1,
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <ListItemIcon sx={{ color: "inherit", minWidth: 36 }}>
                  <KeyboardArrowRightIcon />
                </ListItemIcon>
                <ListItemText primary={category.name} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      <Divider sx={{ my: 2, borderColor: "#d0d0d0", borderWidth: 2 }} />

      <List sx={{ p: 0 }}>
        {visibleCategories.map((category) => {
          const isCategoryExpanded = expandedCategories.includes(category.id);
          const isRootCategory = category.parent === "tümü" || category.parent === "sık-kullanılanlar" || category.parent === "geçmiş";
          const isSelected =
            selectedSubCategory === category.id ||
            (isCategoryExpanded && selectedMainCategory === "tümü" && isRootCategory);

          return (
            <SubCategoryItem
              key={category.id}
              category={category}
              isSelected={isSelected}
              isExpanded={isCategoryExpanded}
              isRootCategory={isRootCategory}
              selectedMainCategory={selectedMainCategory}
              onClick={() => handleSubCategorySelect(category.id)}
              onToggle={() => handleCategoryToggle(category.id)}
            />
          );
        })}
      </List>
    </Box>
  );
};

export default SidebarArea;
